import * as React from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface TooltipIconButtonProps extends Omit<ButtonProps, 'children'> {
  /** Doubles as the accessible name and the tooltip text. */
  label: string;
  icon: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipContent>['side'];
  delayDuration?: number;
}

/**
 * A tooltip is not an accessible name, so `label` sets aria-label too.
 *
 * Carries its own TooltipProvider: Radix throws without one, and requiring
 * callers to remember an ancestor made the button unusable in isolation.
 * Nested providers are supported, so an outer one still wins for grouping.
 */
const TooltipIconButton = React.forwardRef<HTMLButtonElement, TooltipIconButtonProps>(
  (
    {
      label,
      icon,
      side = 'bottom',
      delayDuration = 400,
      variant = 'ghost',
      size = 'icon',
      className,
      ...props
    },
    ref,
  ) => (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            variant={variant}
            size={size}
            aria-label={label}
            className={className}
            {...props}
          >
            {icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent side={side} className="revy-ui">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ),
);
TooltipIconButton.displayName = 'TooltipIconButton';

export { TooltipIconButton };
